/**
 * Custom DryVox Icons
 * 
 * Maritime-themed custom icons matching the DryVox brand aesthetic.
 * Clean lines, minimal design, ocean-inspired.
 */

import { cn } from '@/lib/cn';

interface IconProps {
  className?: string;
  size?: number;
}

/**
 * Dashboard Icon - Ship's Wheel / Helm
 */
export function DashboardIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Outer wheel */}
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
      {/* Inner hub */}
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" fill="none" />
      {/* Spokes */}
      <path d="M12 3V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 15V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M3 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15 12H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Diagonal spokes */}
      <path d="M5.64 5.64L8.88 8.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M15.12 15.12L18.36 18.36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M18.36 5.64L15.12 8.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M8.88 15.12L5.64 18.36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * AI Assistant Icon - Compass with AI spark
 */
export function AIAssistantIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Compass circle */}
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" fill="none" />
      {/* Compass needle - North */}
      <path d="M12 5L14 12L12 14L10 12L12 5Z" fill="currentColor" />
      {/* Compass needle - South */}
      <path d="M12 19L10 12L12 10L14 12L12 19Z" stroke="currentColor" strokeWidth="1.5" fill="none" />
      {/* AI spark dots */}
      <circle cx="12" cy="3" r="1" fill="currentColor" />
      <circle cx="19" cy="8" r="0.75" fill="currentColor" opacity="0.7" />
      <circle cx="5" cy="8" r="0.75" fill="currentColor" opacity="0.7" />
    </svg>
  );
}

/**
 * Sessions/History Icon - Ship's Log Book
 */
export function SessionsIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Book spine */}
      <path 
        d="M4 4C4 3.44772 4.44772 3 5 3H7C7.55228 3 8 3.44772 8 4V20C8 20.5523 7.55228 21 7 21H5C4.44772 21 4 20.5523 4 20V4Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        fill="none" 
      />
      {/* Book pages */}
      <path 
        d="M8 4H18C19.1046 4 20 4.89543 20 6V18C20 19.1046 19.1046 20 18 20H8" 
        stroke="currentColor" 
        strokeWidth="2" 
        fill="none" 
      />
      {/* Log lines */}
      <path d="M11 8H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M11 12H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M11 16H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Settings Icon - Ship's Gear/Cog with waves
 */
export function SettingsIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Main gear */}
      <path 
        d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        fill="none" 
      />
      {/* Gear teeth */}
      <path 
        d="M12 3V5M12 19V21M21 12H19M5 12H3M18.36 5.64L16.95 7.05M7.05 16.95L5.64 18.36M18.36 18.36L16.95 16.95M7.05 7.05L5.64 5.64" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
      />
    </svg>
  );
}

/**
 * Clock Icon - Ship's Chronometer
 */
export function ChronoIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Main clock face */}
      <circle cx="12" cy="13" r="8" stroke="currentColor" strokeWidth="2" fill="none" />
      {/* Top knob */}
      <path d="M12 5V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="2" r="1" fill="currentColor" />
      {/* Clock hands */}
      <path d="M12 13V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 13L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Center dot */}
      <circle cx="12" cy="13" r="1.5" fill="currentColor" />
      {/* Hour markers */}
      <circle cx="12" cy="7" r="0.75" fill="currentColor" opacity="0.5" />
      <circle cx="17" cy="13" r="0.75" fill="currentColor" opacity="0.5" />
      <circle cx="7" cy="13" r="0.75" fill="currentColor" opacity="0.5" />
      <circle cx="12" cy="19" r="0.75" fill="currentColor" opacity="0.5" />
    </svg>
  );
}

/**
 * Sessions Count Icon - Voyage Counter / Ships
 */
export function VoyageIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Ship hull */}
      <path 
        d="M3 17L5 19H19L21 17L19 13H5L3 17Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinejoin="round" 
        fill="none" 
      />
      {/* Mast */}
      <path d="M12 13V5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Flag */}
      <path 
        d="M12 5L17 7L12 9" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinejoin="round" 
        fill="none" 
      />
      {/* Waves */}
      <path 
        d="M2 21C4 21 5 20 7 20C9 20 10 21 12 21C14 21 15 20 17 20C19 20 20 21 22 21" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinecap="round" 
        opacity="0.6" 
      />
    </svg>
  );
}

/**
 * Image Generation Icon - Lighthouse with rays
 */
export function LighthouseIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Lighthouse base */}
      <path 
        d="M8 21L9 14H15L16 21H8Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinejoin="round" 
        fill="none" 
      />
      {/* Lighthouse top */}
      <path 
        d="M9 14V10C9 9.44772 9.44772 9 10 9H14C14.5523 9 15 9.44772 15 10V14" 
        stroke="currentColor" 
        strokeWidth="2" 
        fill="none" 
      />
      {/* Light dome */}
      <path 
        d="M10 9V7C10 5.89543 10.8954 5 12 5C13.1046 5 14 5.89543 14 7V9" 
        stroke="currentColor" 
        strokeWidth="2" 
        fill="none" 
      />
      {/* Light rays */}
      <path d="M12 3V2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M17 5L18 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M7 5L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M19 8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M4 8H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Window */}
      <rect x="11" y="10" width="2" height="2" fill="currentColor" rx="0.5" />
    </svg>
  );
}

/**
 * Plan/Subscription Icon - Ship's Wheel Badge
 */
export function PlanIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Shield shape */}
      <path 
        d="M12 3L20 7V12C20 16.4183 16.4183 20 12 20C7.58172 20 4 16.4183 4 12V7L12 3Z" 
        stroke="currentColor" 
        strokeWidth="2" 
        strokeLinejoin="round" 
        fill="none" 
      />
      {/* Star inside */}
      <path 
        d="M12 7L13.09 10.26L16.5 10.64L14 12.87L14.74 16.18L12 14.27L9.26 16.18L10 12.87L7.5 10.64L10.91 10.26L12 7Z" 
        stroke="currentColor" 
        strokeWidth="1.5" 
        strokeLinejoin="round" 
        fill="none" 
      />
    </svg>
  );
}

/**
 * Logout Icon - Anchor lifting
 */
export function AnchorUpIcon({ className, size = 24 }: IconProps) {
  return (
    <svg 
      width={size} 
      height={size} 
      viewBox="0 0 24 24" 
      fill="none" 
      className={cn('flex-shrink-0', className)}
    >
      {/* Anchor ring */}
      <circle cx="12" cy="5" r="2" stroke="currentColor" strokeWidth="2" fill="none" />
      {/* Anchor shaft */}
      <path d="M12 7V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      {/* Anchor flukes */}
      <path d="M7 20C7 17 9 15 12 15C15 15 17 17 17 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none" />
      {/* Up arrow */}
      <path d="M9 3L12 0L15 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default {
  DashboardIcon,
  AIAssistantIcon,
  SessionsIcon,
  SettingsIcon,
  ChronoIcon,
  VoyageIcon,
  LighthouseIcon,
  PlanIcon,
  AnchorUpIcon,
};

